import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, MapPin, Star, BedDouble, Users, Ruler, ShieldCheck, Wallet, Megaphone } from "lucide-react";
import { fmtPrice, withWatermark } from "@/lib/utils";
import { recordSponsoredClick } from "@/lib/api";

const hh = (h) => `${String(h).padStart(2, "0")}h`;
const pct = (h) => Math.round((1 - h.prix_heure / h.prix_normal) * 100);

const Carousel = ({ images, name, testid }) => {
  const [i, setI] = useState(0);
  const n = images.length;
  const go = (d) => (e) => { e.preventDefault(); e.stopPropagation(); setI((i + d + n) % n); };
  return (
    <div className="relative h-64 md:h-full min-h-[240px] overflow-hidden group bg-neutral-100" data-testid={testid}>
      <img src={withWatermark(images[i])} alt={`${name} - photo ${i + 1}`} className="w-full h-full object-cover object-bottom transition-transform duration-500 group-hover:scale-105" loading="lazy" />
      {n > 1 && (
        <>
          <button type="button" onClick={go(-1)} className="absolute left-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Photo precedente" data-testid={`${testid}-prev`}><ChevronLeft size={18} /></button>
          <button type="button" onClick={go(1)} className="absolute right-2 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-white/90 shadow flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Photo suivante" data-testid={`${testid}-next`}><ChevronRight size={18} /></button>
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5">
            {images.map((_, k) => <span key={k} className={`h-1.5 rounded-full transition-all ${k === i ? "w-5 bg-white" : "w-1.5 bg-white/60"}`} />)}
          </div>
        </>
      )}
    </div>
  );
};

export const DayUseHotelCard = ({ hotel: h, onPick, index = 0 }) => {
  const slots = h.creneaux || [];
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? slots : slots.slice(0, 8);
  return (
    <article className={`rise bg-white rounded-3xl overflow-hidden shadow-[0_2px_20px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_40px_rgba(0,0,0,0.12)] transition-shadow grid grid-cols-1 md:grid-cols-[42%_1fr] ${h.sponsored ? "ring-2 ring-violet-400" : ""}`} style={{ animationDelay: `${index * 0.05}s` }} data-testid={`day-use-card-${h.id}`}>
      <div className="relative">
        <Carousel images={h.images?.length ? h.images : [h.image]} name={h.name} testid={`day-use-carousel-${h.id}`} />
        <span className="absolute top-3 left-3 bg-orange-500 text-white text-xs font-extrabold px-2.5 py-1 rounded-full shadow" data-testid={`day-use-reduction-${h.id}`}>-{h.offre.reduction}%</span>
        {h.sponsored && <span className="absolute top-3 right-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-violet-600 text-white px-2 py-1 rounded-full shadow"><Megaphone size={11} /> Sponsorise</span>}
      </div>

      <div className="p-5 md:p-6 flex flex-col">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Link to={`/hotelleries/${h.id}`} onClick={() => h.sponsored && h.campaign_id && recordSponsoredClick(h.campaign_id)} className="font-display text-xl md:text-2xl font-extrabold hover:text-orange-600 transition-colors" data-testid={`day-use-name-${h.id}`}>{h.name}</Link>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-[var(--ink-soft)] mt-1">
              {h.stars && <span className="inline-flex items-center gap-0.5 text-amber-500">{Array.from({ length: h.stars }).map((_, k) => <Star key={k} size={12} fill="currentColor" />)}</span>}
              <span className="inline-flex items-center gap-1"><MapPin size={13} /> {h.quartier ? `${h.quartier}, ` : ""}{h.city}</span>
              {h.rating_avg ? <span className="inline-flex items-center gap-1 font-semibold text-[var(--ink)]"><span className="bg-emerald-600 text-white text-xs px-1.5 py-0.5 rounded-md">{h.rating_avg}/5</span> {h.rating_count} avis</span> : <span className="text-xs">Nouveau sur Fodindin</span>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[11px] uppercase tracking-wider text-[var(--ink-soft)]">a partir de</div>
            <div className="text-2xl md:text-3xl font-extrabold text-[var(--ink)] leading-none" data-testid={`day-use-price-${h.id}`}>{fmtPrice(h.prix_heure)}</div>
            <div className="text-xs mt-1"><span className="text-emerald-700 font-bold">-{pct(h)}%</span> <span className="line-through text-[var(--ink-soft)]">{fmtPrice(h.prix_normal)}</span> <span className="text-[var(--ink-soft)]">tarif normal</span></div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-3 text-[11px] font-semibold">
          <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full"><ShieldCheck size={12} /> Annulation gratuite</span>
          <span className="inline-flex items-center gap-1 bg-sky-50 text-sky-700 px-2 py-1 rounded-full"><Wallet size={12} /> Paiement a l&apos;hotel</span>
          <span className="inline-flex items-center gap-1 bg-neutral-100 text-[var(--ink-soft)] px-2 py-1 rounded-full">Ouvert {hh(h.offre.heure_debut)} - {hh(h.offre.heure_fin)}</span>
        </div>

        {h.chambres?.length > 0 && (
          <div className="mt-4 flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid={`day-use-rooms-${h.id}`}>
            {h.chambres.map((c, k) => (
              <div key={k} className="shrink-0 flex items-center gap-2 border border-neutral-200 rounded-xl px-2.5 py-1.5 text-xs bg-neutral-50">
                <img src={withWatermark(c.image)} alt={c.nom} className="h-9 w-12 object-cover rounded-md" loading="lazy" />
                <div>
                  <div className="font-bold">{c.nom}</div>
                  <div className="text-[var(--ink-soft)] inline-flex items-center gap-2"><span className="inline-flex items-center gap-0.5"><Users size={10} /> {c.capacite}</span>{c.lits && <span className="inline-flex items-center gap-0.5"><BedDouble size={10} /> {c.lits}</span>}{c.surface_m2 ? <span className="inline-flex items-center gap-0.5"><Ruler size={10} /> {c.surface_m2} m²</span> : null}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-auto pt-4">
          <div className="text-xs font-bold uppercase tracking-wider text-[var(--ink-soft)] mb-2">Creneaux disponibles · {h.date}</div>
          {slots.length === 0 ? (
            <div className="text-sm text-[var(--ink-soft)] bg-neutral-50 rounded-xl px-3 py-2" data-testid={`day-use-full-${h.id}`}>Complet pour ces criteres. Essayez une autre date ou d&apos;autres horaires.</div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid={`day-use-slots-${h.id}`}>
              {visible.map((c) => (
                <button key={`${c.hour}-${c.duree}`} type="button" onClick={() => onPick(h, c)}
                  className="group/slot rounded-xl border-2 border-neutral-200 hover:border-orange-500 hover:bg-orange-50 px-3 py-2 text-left transition-colors"
                  data-testid={`day-use-slot-${h.id}-${c.hour}-${c.duree}`}>
                  <div className="font-extrabold text-sm leading-none">{hh(c.hour)} - {hh(c.end)}</div>
                  <div className="text-[11px] text-[var(--ink-soft)] mt-1">{c.duree} h · <span className="font-bold text-orange-600">{fmtPrice(c.price)}</span>{c.rooms_left <= 2 && <span className="text-red-600 font-semibold"> · {c.rooms_left} rest.</span>}</div>
                </button>
              ))}
              {slots.length > 8 && !showAll && (
                <button type="button" onClick={() => setShowAll(true)} className="rounded-xl border-2 border-dashed border-neutral-300 px-3 py-2 text-sm font-semibold text-[var(--ink-soft)] hover:border-orange-400" data-testid={`day-use-more-${h.id}`}>+{slots.length - 8} creneaux</button>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};
